import clsx from 'clsx';
import React from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import { PER_YEAR } from '@components/pages/Upgrade/constants';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';

import OfferPrice from './OfferPrice';

interface OfferProps {
  className?: string;
  offer: OfferOption;
  onOfferClick: () => void;
  setSelectedOffer: (plan: SelectedOffer) => void;
}

const Offer = ({ className, offer, onOfferClick, setSelectedOffer }: OfferProps) => {
  const { trackEvent } = useEventTracking();

  const {
    billingFrequency,
    code,
    currentPrice,
    isPro,
    isBestValue,
    isMonthlyPriced,
    showMonthlyPriceAsAnnual,
    originalPrice,
    title,
  } = offer;
  const Button = isPro ? ProButton : PrimaryButton;

  const handleOfferClick = () => {
    setSelectedOffer({
      billingFrequency,
      code,
      currentPrice,
      isPro,
      originalPrice,
      title,
    });
    trackEvent(
      click({ clickTarget: ClickTarget.Offer, offerCode: code, clickText: ClickTarget.Subscribe })
    );
    onOfferClick();
  };

  return (
    <div
      id={code}
      className={clsx(
        'relative flex w-full flex-1 flex-col md:max-w-[190px] lg:max-w-[270px]',
        className
      )}
    >
      <span
        className={clsx(
          'h-16 whitespace-nowrap text-center font-suisse-intl-mono text-overline-sm',
          {
            'text-primary-magenta': !isPro,
            'text-pro-gold': isPro,
          }
        )}
      >
        {isBestValue ? 'Best Value' : isPro ? 'Special Offer' : ''}
      </span>
      <h3 className="m-0 text-center font-suisse-works text-body-lg text-black sm:text-headline-xxs">
        {title}
      </h3>
      <OfferPrice
        className={clsx({
          'mb-8': !isMonthlyPriced && !showMonthlyPriceAsAnnual,
        })}
        offer={offer}
      />
      {(isMonthlyPriced || showMonthlyPriceAsAnnual) && (
        <span className="mb-8 whitespace-nowrap text-center font-suisse-screen text-disclosure-md text-grayscale-gray sm:text-disclosure-lg">
          ${showMonthlyPriceAsAnnual ? (currentPrice * 13).toFixed(2) : currentPrice} {PER_YEAR}
        </span>
      )}
      <Button as="button" onClick={handleOfferClick} size="small" className="mt-auto w-full">
        Subscribe
      </Button>
    </div>
  );
};

export default Offer;
